import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

/**
 * AppToast — composant Toast unique, partagé par toute l'application.
 *
 * Monté une seule fois dans main-layout (sous le router-outlet). Les pages
 * utilisent simplement `MessageService.add(...)` (déjà fourni globalement
 * dans app.config) pour afficher un toast — pas besoin de mettre leur propre
 * <p-toast>.
 *
 * Apparence : icône colorée selon `severity` + titre bold + détail muté.
 * Le toast peut afficher un bouton "Annuler" si l'appelant fournit
 * `data: { undo: () => void, undoLabel?: string }`.
 *
 * Exemple d'usage côté feature :
 *   this.messageService.add({
 *     severity: 'success',
 *     summary: 'Patient supprimé',
 *     detail: 'Aminata KONE',
 *     life: 6000,
 *     data: { undo: () => this.restorePatient(id), undoLabel: 'Annuler la suppression' },
 *   });
 */
@Component({
  selector: 'app-toast',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ToastModule],
  template: `
    <p-toast position="top-right" [life]="5000" styleClass="app-toast">
      <ng-template let-message pTemplate="message">
        <div class="flex items-start gap-4 w-full p-1">
          <!-- Icon container with background -->
          <div class="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
               [class]="iconBg(message.severity)">
            <i [class]="iconClass(message)" class="text-xl" aria-hidden="true"></i>
          </div>
          <!-- Content -->
          <div class="flex-1 min-w-0 pt-0.5">
            <div class="font-bold text-ink">{{ message.summary }}</div>
            @if (message.detail) {
              <div class="text-sm text-ink-muted mt-0.5">{{ message.detail }}</div>
            }
            @if (message.data?.undo) {
              <button type="button"
                      class="mt-3 inline-flex min-h-11 items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold
                             bg-chrome text-ink hover:bg-neutral-surface transition-colors"
                      (click)="runUndo(message)">
                <i class="pi pi-undo text-xs"></i>
                {{ message.data?.undoLabel || 'Annuler' }}
              </button>
            }
          </div>
        </div>
      </ng-template>
    </p-toast>
  `,
  styles: [`
    :host ::ng-deep .app-toast { max-width: calc(100vw - 2rem); }
    :host ::ng-deep .app-toast .p-toast-message {
      border-radius: 1rem;
      box-shadow: 0 10px 25px -5px rgb(15 23 42 / 0.10), 0 8px 10px -6px rgb(15 23 42 / 0.06);
      border: 1px solid var(--color-hairline);
      background: var(--color-panel);
    }
    :host ::ng-deep .app-toast .p-toast-message-content { padding: 1rem 1.25rem; }
    :host ::ng-deep .app-toast .p-toast-icon-close,
    :host ::ng-deep .app-toast .p-toast-close {
      background: var(--color-chrome);
      color: var(--color-ink-muted);
      border-radius: 9999px;
      width: 2.75rem;
      height: 2.75rem;
      border: 1px solid var(--color-hairline);
      opacity: 1;
    }
    :host ::ng-deep .app-toast .p-toast-icon-close:hover,
    :host ::ng-deep .app-toast .p-toast-close:hover {
      background: var(--color-neutral-surface);
      color: var(--color-ink);
    }
  `],
})
export class AppToastComponent {
  private readonly messageService = inject(MessageService);

  iconBg(severity?: string): string {
    switch (severity) {
      case 'success': return 'bg-success-surface text-success-text';
      case 'error':   return 'bg-danger-surface text-danger-text';
      case 'warn':    return 'bg-warning-surface text-warning-text';
      default:        return 'bg-primary-surface text-primary-text';
    }
  }

  iconClass(message: any): string {
    if (message?.icon) return message.icon;
    switch (message?.severity) {
      case 'success': return 'pi pi-check-circle';
      case 'error':   return 'pi pi-times-circle';
      case 'warn':    return 'pi pi-exclamation-triangle';
      default:        return 'pi pi-info-circle';
    }
  }

  runUndo(message: any): void {
    try { message?.data?.undo?.(); } finally {
      // close the toast manually so the user gets immediate feedback
      this.messageService.clear();
    }
  }
}
